import React, { useState, useEffect } from 'react';
import { Plus, FileText, Calendar, AlertCircle, Trash2 } from 'lucide-react';
import { supabase } from '../lib/supabase';
import '../styles/RFIGridView.css';

interface RFI {
  id: string;
  rfi_number: number;
  subject: string;
  question: string;
  status: string;
  priority: string;
  created_at: string;
  updated_at: string;
}

interface RFIGridViewProps {
  estimationId: string;
  onSelectRFI: (rfiId: string) => void;
  onCreateNew: () => void;
}

const RFIGridView: React.FC<RFIGridViewProps> = ({ estimationId, onSelectRFI, onCreateNew }) => {
  const [rfis, setRFIs] = useState<RFI[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    loadRFIs();
  }, [estimationId]);

  const loadRFIs = async () => {
    try {
      setIsLoading(true);
      const { data, error } = await supabase
        .from('rfi_documents')
        .select('*')
        .eq('estimation_id', estimationId)
        .order('rfi_number', { ascending: false });

      if (error) throw error;

      setRFIs(data || []);
      console.log(`✅ Loaded ${data?.length || 0} RFIs`);
    } catch (error) {
      console.error('Error loading RFIs:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'draft': return 'var(--text-placeholder)';
      case 'submitted': return '#3b82f6';
      case 'answered': return '#10b981';
      case 'closed': return 'var(--text-muted)';
      default: return 'var(--text-placeholder)';
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'low': return '#10b981';
      case 'medium': return '#f59e0b';
      case 'high': return '#ef4444';
      default: return 'var(--text-placeholder)';
    }
  };

  const handleDeleteRFI = async (rfiId: string, rfiNumber: number, e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent card click

    if (!window.confirm(`Delete RFI #${String(rfiNumber).padStart(3, '0')}? This cannot be undone.`)) {
      return;
    }

    try {
      const { error } = await supabase
        .from('rfi_documents')
        .delete()
        .eq('id', rfiId);

      if (error) throw error;

      console.log('🗑️ Deleted RFI:', rfiNumber);
      
      // Refresh the list
      loadRFIs();
    } catch (error) {
      console.error('Error deleting RFI:', error);
      alert('Failed to delete RFI. Please try again.');
    }
  };

  if (isLoading) {
    return (
      <div className="rfi-grid-container">
        <div className="rfi-grid-loading">
          <FileText size={48} color="var(--text-placeholder)" />
          <p>Loading RFIs...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="rfi-grid-container">
      {/* Header */}
      <div className="rfi-grid-header">
        <div className="rfi-grid-title">
          <FileText size={24} />
          <h2>RFI Manager</h2>
          <span className="rfi-count">{rfis.length} RFI{rfis.length !== 1 ? 's' : ''}</span>
        </div>
        <button className="rfi-create-btn" onClick={onCreateNew}>
          <Plus size={18} />
          New RFI
        </button>
      </div>

      {/* Grid */}
      {rfis.length === 0 ? (
        <div className="rfi-grid-empty">
          <FileText size={64} color="var(--text-faint)" />
          <h3>No RFIs Yet</h3>
          <p>Create your first RFI or ask the AI Assistant to generate one</p>
          <button className="rfi-create-btn-large" onClick={onCreateNew}>
            <Plus size={20} />
            Create RFI
          </button>
        </div>
      ) : (
        <div className="rfi-grid">
          {rfis.map((rfi) => (
            <div
              key={rfi.id}
              className="rfi-card"
              onClick={() => onSelectRFI(rfi.id)}
            >
              {/* Header */}
              <div className="rfi-card-header">
                <span className="rfi-card-number">
                  RFI #{String(rfi.rfi_number).padStart(3, '0')}
                </span>
                <div className="rfi-card-header-right">
                  <div className="rfi-card-badges">
                    <span 
                      className="rfi-badge" 
                      style={{ 
                        background: `${getStatusColor(rfi.status)}20`,
                        color: getStatusColor(rfi.status),
                        borderColor: getStatusColor(rfi.status)
                      }}
                    >
                      {rfi.status}
                    </span>
                    {rfi.priority !== 'medium' && (
                      <span 
                        className="rfi-badge"
                        style={{ 
                          background: `${getPriorityColor(rfi.priority)}20`,
                          color: getPriorityColor(rfi.priority),
                          borderColor: getPriorityColor(rfi.priority)
                        }}
                      >
                        {rfi.priority}
                      </span>
                    )}
                  </div>
                  <button
                    className="rfi-delete-btn"
                    onClick={(e) => handleDeleteRFI(rfi.id, rfi.rfi_number, e)}
                    title="Delete RFI"
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              </div>

              {/* Subject */}
              <h3 className="rfi-card-subject">{rfi.subject}</h3>

              {/* Question Preview */}
              <p className="rfi-card-question">
                {rfi.question.substring(0, 120)}
                {rfi.question.length > 120 ? '...' : ''}
              </p>

              {/* Footer */}
              <div className="rfi-card-footer">
                <div className="rfi-card-date">
                  <Calendar size={14} />
                  {new Date(rfi.created_at).toLocaleDateString()}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default RFIGridView;

